import type { ReactNode, Ref } from "react";
import { Button } from "../../base/Button";
import "./hint-steps.css";

/*
ヒントのはしごの見た目だけを持つ (10/28 代表が案 D を選んだ)。何段まで開いたかは持たず、呼び手が渡す。
レッスンの頁 (HintSteps) は useHintSteps の数を、クラウドエディターの板は章の進みと一緒に持つ数を渡す。
板は開いた数をチューターと api-go (答えを開く条件) にも渡すので、数は外に置いたまま見た目だけをそろえる。

- 左の柱に全部の段の丸を先に並べ、「全部で何段、いまどこまで借りたか」を開く前から見せる
- 開いた段の本文は付箋、次の段は付箋の束の釦。押すと 1 枚はがれてその段の付箋になる
- 黄は「答えに近い」1 段だけ。レッスンは最後のヒント (nearLast)、板は答えの段 (answer) が黄になる
*/
export interface HintLadderProps {
  hints: string[];
  /** 開いた段の数 (0 から hints.length) */
  opened: number;
  /** 束の釦を押したとき。次の 1 段を開くのは呼び手 */
  onOpenNext: () => void;
  id?: string;
  className?: string;
  /** 見出し。既定は「ヒント」 */
  title?: string;
  /** 見出しの下の 1 行。語の途中で折らないよう句ごとに渡す */
  lead?: string[];
  visual?: ReactNode;
  /** 最後のヒントを「答えに近い」として黄にするか。言い切れない所 (板) では false */
  nearLast?: boolean;
  /**
   * はしごの最後に置く「答え」の段。ヒントを全部開くと中身 (釦など) を黄の付箋で出し、
   * それまでは黄の丸と「答え」の札だけを出す
   */
  answer?: ReactNode;
  renderText?: (text: string) => ReactNode;
  lastItemRef?: Ref<HTMLLIElement>;
}

export function HintLadder({
  hints,
  opened: openedRaw,
  onOpenNext,
  id,
  className,
  title = "ヒント",
  lead,
  visual,
  nearLast = false,
  answer,
  renderText = (text) => text,
  lastItemRef,
}: HintLadderProps) {
  const total = hints.length;
  if (total === 0) return null;
  const opened = Math.min(Math.max(openedRaw, 0), total);
  const hasAnswer = answer !== undefined;
  // 黄の 1 段の位置 (はしご全体の何段目か、0 始まり)。無ければ -1
  const nearIndex = hasAnswer ? total : nearLast && total > 1 ? total - 1 : -1;
  const steps = total + (hasAnswer ? 1 : 0);
  const remaining = total - opened;
  const nextIsNear = opened === nearIndex;
  // 束の下にのぞく紙の縁は最大 2 枚。答えの段も束の下の 1 枚として数え、黄の段が見える深さにあれば、その縁を黄にする
  const edges = Math.min(steps - opened - 1, 2);
  const hintsKey = hints.join("\u0000");
  const status =
    opened === 0
      ? "まだ開いていません"
      : opened === total && !hasAnswer
        ? "全部開きました"
        : `${opened} / ${steps} 段まで`;

  return (
    <div id={id} className={`hs space-y-4${className ? ` ${className}` : ""}`}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="hs-title m-0 font-sans text-xl font-bold leading-none text-main">{title}</h3>
        <p data-hint-status className="m-0 hs-sub text-[15px] font-bold tabular-nums">
          {status}
        </p>
      </div>
      {lead && (
        <p className="m-0 hs-sub hs-lead text-[17px] sm:text-base leading-relaxed">
          {lead.map((phrase) => (
            <span key={phrase} className="inline-block">
              {phrase}
            </span>
          ))}
        </p>
      )}
      {visual}
      <ol className="list-none m-0 p-0 pt-2" aria-live="polite">
        {hints.map((hint, i) => {
          const state = i < opened ? "open" : i === opened ? "next" : "later";
          const near = i === nearIndex;
          return (
            <li
              key={`${i}-${hintsKey}`}
              ref={i === total - 1 ? lastItemRef : undefined}
              tabIndex={lastItemRef && i === total - 1 && state === "open" ? -1 : undefined}
              className="hs-rung rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              data-state={state}
              data-near={near ? "" : undefined}
              data-fresh={state === "open" && i === opened - 1 ? "" : undefined}
            >
              <span className="hs-dot" aria-hidden="true">
                {i + 1}
              </span>
              <div className="min-w-0">
                {state === "open" && (
                  <div className={near ? "hs-note hs-note--near" : "hs-note"}>
                    <p className="hs-note-no">
                      <span>ヒント {i + 1}</span>
                      {near && <span className="hs-near-tag">答えに近い</span>}
                    </p>
                    <p data-hint-body className="hs-body">
                      {renderText(hint)}
                    </p>
                  </div>
                )}
                {state === "next" && (
                  <div className="hs-pile" style={{ paddingBottom: edges * 6 }}>
                    {Array.from({ length: edges }, (_, k) => {
                      const depth = edges - k;
                      return (
                        <span
                          key={depth}
                          aria-hidden="true"
                          className="hs-pile-edge"
                          data-near={opened + depth === nearIndex ? "" : undefined}
                          style={{ top: depth * 6, left: depth * 4, right: depth * 4 }}
                        />
                      );
                    })}
                    <Button
                      data-hint-next
                      data-near={nextIsNear ? "" : undefined}
                      variant="ghost"
                      size="lg"
                      className="hs-flip"
                      onClick={onOpenNext}
                    >
                      <span>{nextIsNear ? "最後の 1 枚をめくる" : `${opened + 1} 枚目をめくる`}</span>
                      {nextIsNear ? (
                        <span className="hs-near-tag">答えに近い</span>
                      ) : (
                        <span className="text-[15px] font-normal opacity-80">残り {remaining} 枚</span>
                      )}
                    </Button>
                  </div>
                )}
                {state === "later" && (
                  <p className="hs-later">
                    <span>ヒント {i + 1}</span>
                    {near && <span className="hs-near-tag">答えに近い</span>}
                  </p>
                )}
              </div>
            </li>
          );
        })}
        {hasAnswer && (
          <li
            data-hint-answer
            className="hs-rung"
            data-state={opened === total ? "next" : "later"}
            data-near=""
          >
            <span className="hs-dot" aria-hidden="true">
              {steps}
            </span>
            <div className="min-w-0">
              {opened === total ? (
                <div className="hs-note hs-note--near">
                  <p className="hs-note-no">
                    <span className="hs-near-tag">答え</span>
                  </p>
                  {answer}
                </div>
              ) : (
                <p className="hs-later">
                  <span className="hs-near-tag">答え</span>
                </p>
              )}
            </div>
          </li>
        )}
      </ol>
    </div>
  );
}
